import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { projectTypesApi } from '../../../../api/projects';
import { ProjectType } from '../../../../types';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../components/ui/organisms/dialog';
import { Loading } from '../components';
import { getErrorMessage } from '../utils';

function RenameTypeDialog({ type }: { type: ProjectType }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(type.name);

  const rename = useMutation({
    mutationFn: () => projectTypesApi.update(type.id, { name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setName(type.name); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="Rename type">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Rename Type</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && rename.mutate()}
          />
          {rename.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(rename.error, 'Failed to rename type')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => rename.mutate()} disabled={!name.trim() || rename.isPending}>
              {rename.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectTypeCard({ type }: { type: ProjectType }) {
  const queryClient = useQueryClient();
  const [newPhaseName, setNewPhaseName] = useState('');

  const addPhase = useMutation({
    mutationFn: (name: string) => projectTypesApi.addPhase(type.id, { name }),
    onSuccess: () => {
      setNewPhaseName('');
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const deletePhase = useMutation({
    mutationFn: (phaseId: string) => projectTypesApi.deletePhase(type.id, phaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const deleteType = useMutation({
    mutationFn: () => projectTypesApi.delete(type.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const handleAddPhase = () => {
    if (newPhaseName.trim()) addPhase.mutate(newPhaseName.trim());
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-900 dark:text-white">{type.name}</p>
        <div className="flex items-center gap-0.5">
          <RenameTypeDialog type={type} />
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            title="Delete type"
            disabled={deleteType.isPending}
            onClick={() => deleteType.mutate()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {type.phases.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No phases yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {type.phases.map((phase) => (
            <span
              key={phase.id}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300"
            >
              {phase.name}
              <button
                className="text-slate-400 hover:text-destructive transition-colors leading-none"
                onClick={() => deletePhase.mutate(phase.id)}
                disabled={deletePhase.isPending}
                title="Remove phase"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Add a phase…"
          value={newPhaseName}
          onChange={(e) => setNewPhaseName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddPhase()}
          className="h-8 text-sm"
        />
        <Button
          size="sm"
          className="h-8 px-2.5"
          disabled={!newPhaseName.trim() || addPhase.isPending}
          onClick={handleAddPhase}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function CreateTypeDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => projectTypesApi.create({ name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-types'] });
      setName('');
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          New Type
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Project Type</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="e.g. Software Development"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && create.mutate()}
          />
          {create.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(create.error, 'Failed to create type')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create Type'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectTypesTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['project-types'],
    queryFn: () => projectTypesApi.list().then((r) => r.data),
  });
  const types: ProjectType[] = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Define reusable project types and their lifecycle phases.
        </p>
        <CreateTypeDialog />
      </div>

      {isLoading ? (
        <Loading />
      ) : types.length === 0 ? (
        <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-200 dark:border-slate-800">
          <p className="text-sm text-muted-foreground">No project types yet. Create your first one.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {types.map((type) => (
            <ProjectTypeCard key={type.id} type={type} />
          ))}
        </div>
      )}
    </div>
  );
}
